import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect, useDispatch } from 'react-redux'
import Hammer from 'react-hammerjs'
import { sort } from 'fast-sort'
import { hideModal } from '../../actions/_modal'
import { postToLoggingService } from '../../actions/logging'
import ModalFullScreen from '../mini/ModalFullScreen'
import UserAvatar from '../mini/UserAvatar'
import CloseModal from '../styled/CloseModal'
import withApiData from '../HOC/withApiData'
import useFrameworkSkills from '../../hooks/skills/useFrameworkSkills'
import useWorkSkills from '../../hooks/skills/useWorkSkills'
import SkillCard from './SkillsTrends/SkillCard'
import {
  ModalContainer,
  ModalTop,
  ModalBody,
} from './SkillsTrends/_Styled'
import { H3Alternate, Title } from '../styled/typography/typography'
import FlexAlignCenter from '../styled/FlexAlignCenter'
import { fetchStudentProfile } from '../../actions/studentprofile'
import {
  getJourneysGroupedByMasterCourse as getStudentJourneysGroupedByMasterCourse
} from '../../selectors/studentprofile'
import {
  getJourneysGroupedByMasterCourse as getUserJourneysGroupedByMasterCourse
} from '../../selectors/usercourses'
import { getMasterCourseSelectOptionsFromEnrollments } from '../../utils/courses'
import { getFrameworkSkillWithGroup } from '../../utils/skills'
import useLexicon from '../../hooks/useLexicon'
import MasterCourseSelect from '../select/MasterCourse/MasterCourseSelect'

const SkillsTrends = ({
  apiData,
  skillId,
  skillIds,
  workSkillId,
  source,
  student,
  isMentor,
  actionOnClose,
  masterCourseId,
  courses,
  isFetchingCourses,
  academicYears,
  userCompiledQuestId,
  autoOpenUcqChecks,
  currentStudentProfileId
}) => {
  const dispatch = useDispatch()
  const lexicon = useLexicon()

  const [selectedMasterCourseId, setSelectedMasterCourseId] = useState(
    masterCourseId
  )

  const { frameworkSkills, frameworkSkillGroups } = useFrameworkSkills()
  const workSkills = useWorkSkills()

  let skills
  if (workSkillId) {
    skills = workSkills
  } else {
    const skillIdsWithDefault = skillIds ?? frameworkSkills.map(s => s.id)
    const skillsWithGroup = skillIdsWithDefault.map(
      id => getFrameworkSkillWithGroup(id, frameworkSkills, frameworkSkillGroups)
    )
    skills = sort(skillsWithGroup).asc([fs => fs.group.name, fs => fs.name])
  }

  const [selectedSkillCardIndex, setSelectedSkillCardIndex] = useState(() =>
    (workSkillId || skillId)
      ? skills.findIndex(s => s.id === (workSkillId ?? skillId))
      : 0
  )
  const [isClosing, setIsClosing] = useState(false)

  const masterCourseOptions = getMasterCourseSelectOptionsFromEnrollments(
    courses
  )

  useEffect(() => {
    // need to get the courses that the student is in
    if (isMentor && currentStudentProfileId !== student.id) {
      dispatch(fetchStudentProfile(student.id))
    }

    logViewSkillsTrends()
  }, [])

  const nextSkill = () => {
    setSelectedSkillCardIndex(ssci => (ssci + 1) % skills.length)
  }

  const prevSkill = () => {
    setSelectedSkillCardIndex(ssci =>
      ssci === 0 ? skills.length - 1 : (ssci - 1)
    )
  }

  const skillCardSwipe = e => {
    if (e.direction === 2) {
      nextSkill()
    } else {
      prevSkill()
    }
  }

  const closeTheModal = () => {
    setIsClosing(true)

    setTimeout(() => {
      dispatch(hideModal())

      if (actionOnClose) {
        if (actionOnClose.action) {
          dispatch(
            actionOnClose.action(actionOnClose.dataObject)
          )
        } else {
          actionOnClose()
        }
      }

    }, 500)
  }

  const logViewSkillsTrends = () => {
    const data = {
      action: 'view_skills_trends',
      source,
      student: isMentor ? {
        id: student.id,
        name: student.name
      } : null,
      master_course: {
        id: masterCourseId
      }
    }

    dispatch(
      postToLoggingService('track_skill_check', data)
    )
  }

  return (
    <div className='skills-check-modal-container'>
      <ModalFullScreen
        padding={'200px 0 0'}
        allowEscape
        allowCloseOnExternalClick
        closeCallback={closeTheModal}
        isClosing={isClosing}
        backgroundColor="rgba(36,54,70,0.4)"
      >
        <CloseModal
          size={18}
          onClick={closeTheModal}
        />

        <Hammer onSwipe={skillCardSwipe}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalTop>
              {isMentor ? (
                <FlexAlignCenter>
                  <UserAvatar
                    size={38}
                    borderRadius={5}
                    photoUrl={student.photo_url}
                  />
                  <H3Alternate>{student.name}</H3Alternate>
                </FlexAlignCenter>
              ) : (
                <Title>
                  {workSkillId ? 'Work ' : 'Learning '}
                  {lexicon.skill}s
                </Title>
              )}
              
              {masterCourseOptions && masterCourseOptions.length > 0 && (
                <MasterCourseSelect
                  masterCourses={masterCourseOptions}
                  selected={selectedMasterCourseId}
                  academicYears={academicYears}
                  onMasterCourseSelect={setSelectedMasterCourseId}
                  width="250px"
                />
              )}
            </ModalTop>

            <ModalBody>
              <SkillCard
                dispatch={dispatch}
                skill={skills.length > 0 ? skills[selectedSkillCardIndex] : null}
                isWorkSkill={!!workSkillId}
                isMentor={isMentor}
                student={student}
                isFetchingCourses={isFetchingCourses}
                selectedMasterCourseId={selectedMasterCourseId}
                academicYears={academicYears}
                autoOpenUcqId={autoOpenUcqChecks && userCompiledQuestId}
                nSkills={skills.length}
                selectedSkillCardIndex={selectedSkillCardIndex}
                setSelectedSkillCardIndex={setSelectedSkillCardIndex}
                prevSkill={prevSkill}
                nextSkill={nextSkill}
              />
            </ModalBody>
          </ModalContainer>
        </Hammer>
      </ModalFullScreen>
    </div>
  )
}

SkillsTrends.propTypes = {
  apiData: PropTypes.object.isRequired,
  skillId: PropTypes.number,
  skillIds: PropTypes.number,
  workSkillId: PropTypes.number,
  source: PropTypes.string,
  student: PropTypes.object,
  userCompiledQuestId: PropTypes.number,
  isMentor: PropTypes.bool,
  actionOnClose: PropTypes.object,
  courses: PropTypes.array,
  isFetchingCourses: PropTypes.bool,
  masterCourseId: PropTypes.number.isRequired,
  academicYears: PropTypes.array.isRequired,
  autoOpenUcqChecks: PropTypes.bool // automatically open the tooltips for any checks from the given user compiled quest
}

const mapStateToProps = (state, ownProps) => ({
  courses: ownProps.isMentor
    ? getStudentJourneysGroupedByMasterCourse({
        state,
        excludeNonAcademicYearCourses: true
      })
    : getUserJourneysGroupedByMasterCourse({
        state,
        excludeNonAcademicYearCourses: true
      }),
  currentStudentProfileId: state.studentprofile.student?.id,
  isFetchingCourses: ownProps.isMentor
    ? state.studentprofile.isFetching
    : state.usercourses.isFetchingCourses,
  academicYears: state.academicyears.items
})

export default connect(mapStateToProps)(withApiData(SkillsTrends))
